/**
 * Event-based toast bridge for non-React code (e.g., queryClient, axios interceptors).
 *
 * React's useToast() hook can only be used inside components. This module provides
 * a simple event emitter that the ToastProvider subscribes to, allowing code outside
 * the React tree to trigger toast notifications.
 *
 * Usage from non-React code:
 *   import { emitToast } from '../lib/toastEvents';
 *   emitToast({ type: 'error', title: 'Error', message: 'Something went wrong' });
 */

export interface ToastEvent {
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
}

type ToastListener = (event: ToastEvent) => void;

const listeners: Set<ToastListener> = new Set();

/** Subscribe to toast events. Returns an unsubscribe function. */
export function onToast(listener: ToastListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Emit a toast event from anywhere (non-React code included). */
export function emitToast(event: ToastEvent): void {
  listeners.forEach((listener) => listener(event));
}
